import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { createContext, useContext } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { type AssignmentFold, NO_ASSIGNMENTS, type OpenAssignment } from "@/hooks/dispatch/assignments";
import { type DispatchTableRow, rowAssignment } from "@/hooks/dispatch/dispatchTableRow";
import type { AssignmentTarget } from "@/store/types";
import { DISPATCH_ASSIGNED } from "./dispatchColors";
import { formatRelativeTime } from "./format";

/**
 * What the table's cells read about the reassignment prototype: the open assignments, who the viewer is, and how to
 * name a target and a sender. Empty while the prototype is off, so no cell changes.
 */
export type DispatchAssignments = {
  fold: AssignmentFold;
  me: string;
  /** The page's minute clock, for the chip's relative time. */
  now: number;
  /** What a chip calls a target: a member's name or "you", a view's name, or a Driver Manager code. */
  labelFor: (target: AssignmentTarget) => string;
  /** A member's name by membership id, or `null` when the store holds none. */
  nameOf: (membershipId: string) => string | null;
};

const NO_DISPATCH_ASSIGNMENTS: DispatchAssignments = {
  fold: NO_ASSIGNMENTS,
  me: "",
  now: 0,
  labelFor: () => "",
  nameOf: () => null,
};

/**
 * The reassignment prototype's state, read by the dot and Message cells. A context rather than a column prop, as with
 * the search highlight, so a handoff re-renders the cells that read it instead of rebuilding every column.
 */
export const DispatchAssignmentContext = createContext<DispatchAssignments>(NO_DISPATCH_ASSIGNMENTS);

/** Whether an open assignment hands the row to someone other than the viewer, which paints its dot purple. */
export function assignedElsewhere(open: OpenAssignment | null, me: string): boolean {
  return open !== null && !(open.assignee.kind === "member" && open.assignee.membershipId === me);
}

/** The newest open assignment on a row, as the cells read it from the context. */
export function useRowAssignment(row: DispatchTableRow): { open: OpenAssignment | null; me: string } {
  const { fold, me } = useContext(DispatchAssignmentContext);
  return { open: fold.byTarget.size === 0 ? null : rowAssignment(row, fold), me };
}

/** A name's first word: "from Sam" on the chip. */
function firstName(name: string): string {
  return name.split(/\s+/)[0] ?? name;
}

/**
 * The purple chip ahead of a row's message while it carries an open assignment: who it went to and who sent it, with
 * the note and how long ago in the tooltip. Renders nothing for a row with no open assignment.
 */
export function AssignmentChip({ row }: { row: DispatchTableRow }) {
  const { fold, me, now, labelFor, nameOf } = useContext(DispatchAssignmentContext);
  const open = fold.byTarget.size === 0 ? null : rowAssignment(row, fold);
  if (!open) return null;
  const sender = open.actorMembershipId
    ? open.actorMembershipId === me
      ? "you"
      : (nameOf(open.actorMembershipId) ?? "someone")
    : "someone";
  const age = formatRelativeTime(open.assignedAt, now);
  const tooltip = (
    <Box>
      <Box>
        {labelFor(open.assignee)}, from {sender}
        {age ? `, ${age.short === "now" ? "just now" : `${age.short} ago`}` : ""}
      </Box>
      {open.note && <Box sx={{ mt: 0.5, fontStyle: "italic" }}>“{open.note}”</Box>}
    </Box>
  );
  return (
    <Tooltip title={tooltip} placement="bottom-start">
      <Box
        component="span"
        data-sb={TestIds.dispatch.assignmentChip}
        sx={{
          flex: "none",
          display: "inline-flex",
          alignItems: "center",
          maxWidth: 220,
          height: 20,
          px: 0.75,
          borderRadius: 10,
          bgcolor: DISPATCH_ASSIGNED.chipTint,
          border: `1px solid ${DISPATCH_ASSIGNED.chipBorder}`,
          color: DISPATCH_ASSIGNED.accent,
          fontSize: "0.6875rem",
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {labelFor(open.assignee)} · from {sender === "you" || sender === "someone" ? sender : firstName(sender)}
      </Box>
    </Tooltip>
  );
}
